"use client";

import { PageHeader } from "@/components/common/PageHeader";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Loading } from "@/components/ui/loading";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { ColumnDef } from "@tanstack/react-table";
import {
  Check,
  Circle,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Task } from "../types";

import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/features/auth/context/AuthContext";
import { cn } from "@/lib/utils";

type StatusFilter = "all" | "pending" | "in_progress" | "completed";

export default function TaskList() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const { toast } = useToast();
  const navigate = useNavigate();
  const { profile } = useAuth();

  useEffect(() => {
    async function fetchTasks() {
      try {
        if (profile.anonymous) throw new Error("No user found");

        const { data: userProfile, error: profileError } = await supabase
          .from("profiles")
          .select("company_id")
          .eq("id", profile.id)
          .single();

        if (profileError) throw profileError;
        if (!userProfile?.company_id) throw new Error("No company_id found");

        // Get tasks with their assignments
        const { data: tasksData, error: tasksError } = await supabase
          .from("tasks")
          .select(
            `
            *,
            task_assignments (
              user_id,
              profiles (
                given_name,
                family_name,
                avatar
              )
            )
          `
          )
          .eq("company_id", userProfile.company_id)
          .order("created_at", { ascending: false });

        if (tasksError) throw tasksError;
        setTasks(tasksData || []);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    }

    if (!profile.anonymous) {
      fetchTasks();
    }
  }, [profile]);

  const columns: ColumnDef<Task>[] = [
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        return status === "completed" ? (
          <Check size={16} className="text-green-500" />
        ) : (
          <Circle size={16} className="text-gray-400" />
        );
      }
    },
    {
      accessorKey: "name",
      header: "Aufgabe"
    },
    {
      accessorKey: "due_date",
      header: "Fälligkeitsdatum",
      cell: ({ row }) => {
        const date = row.getValue("due_date") as string;
        return date ? new Date(date).toLocaleDateString("de-DE") : "-";
      }
    },
    {
      accessorKey: "priority",
      header: "Priorität",
      cell: ({ row }) => {
        const priority = row.getValue("priority") as string;
        return (
          <span
            className={`font-medium ${
              priority === "high"
                ? "text-red-500"
                : priority === "medium"
                  ? "text-yellow-500"
                  : "text-green-500"
            }`}
          >
            {priority === "high"
              ? "Hoch"
              : priority === "medium"
                ? "Mittel"
                : "Niedrig"}
          </span>
        );
      }
    },
    {
      accessorKey: "task_assignments",
      header: "Zugewiesene",
      cell: ({ row }) => {
        const assignments = row.getValue("task_assignments") as Array<{
          user_id: string;
          profiles: {
            given_name: string;
            family_name: string;
            avatar: string | null;
          };
        }>;
        return (
          <div className="flex flex-wrap gap-1">
            {assignments?.map((assignment) => (
              <Avatar key={assignment.user_id} className="h-6 w-6">
                <AvatarImage src={assignment.profiles?.avatar || undefined} />
                <AvatarFallback>
                  {`${assignment.profiles?.given_name[0]}${assignment.profiles?.family_name[0]}`}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/tasks/${row.original.id}/edit`);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTaskToDelete(row.original);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  const handleExport = () => {
    // Implement export functionality
    console.log("Exporting tasks...");
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;

    try {
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", taskToDelete.id);

      if (error) throw error;

      setTasks(tasks.filter((task) => task.id !== taskToDelete.id));
      toast({
        title: "Aufgabe gelöscht",
        description: "Die Aufgabe wurde erfolgreich gelöscht."
      });
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Fehler",
        description: "Die Aufgabe konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  // Filter tasks based on selected status
  const filteredTasks = tasks.filter((task) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "pending")
      return task.status === "pending" || task.status === null;
    return task.status === statusFilter;
  });

  const statusOptions = [
    { value: "all" as StatusFilter, label: "Alle", count: tasks.length },
    {
      value: "pending" as StatusFilter,
      label: "Ausstehend",
      count: tasks.filter((t) => t.status === "pending" || t.status === null)
        .length
    },
    {
      value: "in_progress" as StatusFilter,
      label: "In Bearbeitung",
      count: tasks.filter((t) => t.status === "in_progress").length
    },
    {
      value: "completed" as StatusFilter,
      label: "Abgeschlossen",
      count: tasks.filter((t) => t.status === "completed").length
    }
  ];

  if (loading) return <Loading text="Aufgaben laden..." />;

  if (error)
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-red-500">Fehler: {error}</div>
      </div>
    );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        breadcrumbs={[
          { title: "Startseite", href: "/" },
          { title: "Aufgaben" }
        ]}
      />

      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">Aufgaben</h1>
            <p className="text-gray-500">
              Verwalte deine Aufgaben und verfolge deren Fortschritt
            </p>
          </div>
          <Button onClick={() => navigate("/tasks/new")}>
            <Plus className="mr-2 h-4 w-4" />
            Neue Aufgabe
          </Button>
        </div>

        {/* Status Filter Button Group */}
        <div className="mb-6">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            {statusOptions.map((option, index) => (
              <Button
                key={option.value}
                variant={statusFilter === option.value ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(option.value)}
                className={cn(
                  "flex items-center gap-2 border-r-0 focus:z-10",
                  index === 0 && "rounded-r-none",
                  index === statusOptions.length - 1 &&
                    "rounded-l-none border-r",
                  index > 0 &&
                    index < statusOptions.length - 1 &&
                    "rounded-none"
                )}
              >
                {option.label}
                <Badge variant="secondary" className="ml-1 px-2 py-0 text-xs">
                  {option.count}
                </Badge>
              </Button>
            ))}
          </div>
        </div>

        {filteredTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {statusFilter === "all"
              ? "Keine Aufgaben gefunden"
              : `Keine ${statusOptions.find((opt) => opt.value === statusFilter)?.label.toLowerCase()} Aufgaben gefunden`}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredTasks}
            searchKey="name"
            exportData={handleExport}
            filterPlaceholder="Aufgaben filtern..."
            onRowClick={(row) => navigate(`/tasks/${row.original.id}/edit`)}
          />
        )}

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aufgabe löschen</DialogTitle>
              <DialogDescription>
                Bist du sicher, dass du diese Aufgabe löschen möchtest? Diese
                Aktion kann nicht rückgängig gemacht werden.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
